import _isEmpty from 'lodash/isEmpty';

import actionTypes from './actionTypes';
import {
  getClientWishlists,
  getClientSingleWishlist,
  createWishlistForClient,
  addProductToWishlist,
} from '../services/index';
import { farfetchProductDetailsService, inventoryStatusService } from '../../../config/api';
import { mapProductDataForWishlist } from '../utils';

/**
 * Create new wishlist with provided title and product
 * @param {string} clientId
 * @param {string} wishlistTitle
 * @param {object} productData
 * @returns {(function(*): Promise<void>)|*}
 */
export const createWishlist = (clientId, wishlistTitle, productData) => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENT_WISHLIST_LOADING,
  });

  const { data, error } = await createWishlistForClient(
    clientId,
    wishlistTitle,
    mapProductDataForWishlist(productData),
  );

  if (error) {
    dispatch({
      type: actionTypes.CLIENT_WISHLIST_ERROR,
      payload: error,
    });

    return;
  }

  dispatch({
    type: actionTypes.CLIENT_ADD_WISHLIST_SUCCESS,
    payload: data,
  });
};

/**
 * Add provided product to wishlist
 * @param {string} clientId
 * @param {string} wishlistId
 * @param {object} productData
 * @returns {(function(): Promise<void>)|*}
 */
export const addToWishlist = (clientId, wishlistId, productData) => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENT_WISHLIST_LOADING,
  });

  const { data, error } = await addProductToWishlist(clientId, wishlistId, mapProductDataForWishlist(productData));

  if (error) {
    dispatch({
      type: actionTypes.CLIENT_WISHLIST_ERROR,
    });

    return;
  }

  dispatch({
    type: actionTypes.CLIENT_ADD_TO_WISHLIST_SUCCESS,
    payload: data,
  });
};

/**
 * @param clientId
 * @returns {(function(*): Promise<void>)|*}
 */
export const getWishlistList = (clientId) => async (dispatch, getState) => {
  const { wishlists } = getState().clientsModule.wishlist;

  // If we already have loaded wishlists list for this client use it
  if (wishlists.length) {
    const isLoaded = wishlists.every((wishlist) => wishlist.customerId === clientId);

    if (isLoaded) {
      dispatch({
        type: actionTypes.CLIENT_GET_WISHLIST_LIST_SUCCESS,
        payload: wishlists,
      });

      return;
    }
  }

  dispatch({
    type: actionTypes.CLIENT_WISHLIST_LOADING,
  });

  const { data, error } = await getClientWishlists(clientId);

  if (error) {
    dispatch({
      type: actionTypes.CLIENT_WISHLIST_ERROR,
      payload: error,
    });

    return;
  }

  dispatch({
    type: actionTypes.CLIENT_GET_WISHLIST_LIST_SUCCESS,
    payload: data,
  });
};

export const getProductsForWishlist = (productsIds) => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENT_GET_PRODUCTS_FOR_WISHLIST_LOADING,
  });

  const { data: productsDetails } = await farfetchProductDetailsService.get(`/pal/sku?skuIds=${productsIds}`);
  const { data: inventoryData } = await inventoryStatusService.post('/retail/inventory', { pimSkuIds: productsIds });

  dispatch({
    type: actionTypes.CLIENT_GET_PRODUCTS_FOR_WISHLIST_SUCCESS,
    payload: {
      productsDetails,
      inventoryData,
    },
  });
};

export const resetWishlistData = () => async (dispatch) => {
  dispatch({
    type: actionTypes.CLIENT_RESET_WISHLIST_DATA,
  });
};

/**
 * @param {string} clientId
 * @param {string} wishlistId
 * @returns {(function(*): Promise<void>)|*}
 */
export const getWishlist = (clientId, wishlistId) => async (dispatch, getState) => {
  const { wishlists } = getState().clientsModule.wishlist;

  // If we already have loaded wishlists list, return single wishlist data from it
  if (wishlists.length) {
    const loadedWishlist = wishlists.find((wishlist) => wishlist.wishlistId === wishlistId);

    if (!_isEmpty(loadedWishlist)) {
      dispatch({
        type: actionTypes.CLIENT_GET_WISHLIST_SUCCESS,
        payload: loadedWishlist,
      });

      return;
    }
  }

  dispatch({
    type: actionTypes.CLIENT_WISHLIST_LOADING,
  });

  const { data, error } = await getClientSingleWishlist(clientId, wishlistId);

  if (error) {
    dispatch({
      type: actionTypes.CLIENT_WISHLIST_ERROR,
      payload: error,
    });

    return;
  }

  dispatch({
    type: actionTypes.CLIENT_GET_WISHLIST_SUCCESS,
    payload: data,
  });
};
